import type { UpdateLoginFlowBody } from '@ory/client';
import { useEffect, useMemo, useState } from 'react';
import { Loader } from '@/components/Loading';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { OryForm } from '@/features/auth/components/Form';
import { useReauthFlow } from '@/features/auth/hooks/useReauthFlow';
import { useAuthStore } from '@/lib/auth';
import { NodesAdapter } from '@/lib/auth/ory/adapters/nodes-adapter';
import { authService } from '@/lib/auth/ory/service';
import { defaultNS, useTranslation } from '@/lib/i18n';
type AdditionalAuthGateProps = {
  /**
   * 追加認証成功後に実行するコールバック
   */
  onAuthSuccess?: () => Promise<void> | void;
  /**
   * フローを自動的に初期化するかどうか
   * @default true
   */
  autoInit?: boolean;
};

/**
 * AdditionalAuthGate
 *
 * AAL2のLogin Flow（challenge）を描画するコンポーネント
 */
export function AdditionalAuthGate({
  onAuthSuccess,
  autoInit = true
}: AdditionalAuthGateProps) {
  const {
    t: tOry
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.ory'
  });
  const {
    downloadSignedCookies,
    refreshUserSession
  } = useAuthStore();
  const [selectedGroup, setSelectedGroup] = useState<'totp' | 'code' | null>(null);
  const [preferredMethod, setPreferredMethod] = useState<'totp' | 'code' | null>(null);
  const {
    flow,
    isPending,
    isInitializing,
    onSubmit,
    initFlow
  } = useReauthFlow({
    enabled: true,
    reauthType: 'aal2',
    onSuccess: async () => {
      // 追加認証成功時にセッションクッキーをダウンロードしてセッションをリフレッシュ
      try {
        await downloadSignedCookies();
        await refreshUserSession();
      } catch (error) {
        console.error('Failed to refresh session after additional auth:', error);
      }
      await onAuthSuccess?.();
    }
  });

  // flowを初期化
  useEffect(() => {
    if (!autoInit) return;
    if (!flow && !isInitializing) {
      initFlow().catch(e => {
        console.error('Failed to init additional auth flow:', e);
      });
    }
  }, [autoInit, flow, isInitializing, initFlow]);
  const uiValue = useMemo(() => {
    return flow ? new NodesAdapter(flow.ui.nodes) : null;
  }, [flow]);
  const mfaMethods = useMemo(() => {
    return uiValue?.mfaMethodGroupIds ?? [];
  }, [uiValue]);

  // session.mfa.preferred_method を初期選択に使う
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authService.getMfaCredentials();
        if (!alive) return;
        setPreferredMethod(res.preferredMethod);
      } catch (e) {
        if (!alive) return;
        console.error('Failed to load preferred mfa method:', e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // challenge表示
  const shouldSelectMethod = uiValue?.shouldShowMfaMethodSelect ?? false;
  const effectiveSelectedGroup = useMemo(() => {
    if (!shouldSelectMethod) return null;
    const mfaMethods = uiValue?.mfaMethodGroupIds ?? [];

    // 1) ユーザー選択（有効な場合）
    if (selectedGroup && mfaMethods.includes(selectedGroup)) {
      return selectedGroup;
    }

    // 2) preferred method（有効な場合）
    if (preferredMethod && mfaMethods.includes(preferredMethod)) {
      return preferredMethod;
    }

    // 3) フォールバック優先順（totp -> code）
    if (mfaMethods.includes('totp')) return 'totp';
    if (mfaMethods.includes('code')) return 'code';
    return mfaMethods[0] ?? null;
  }, [preferredMethod, selectedGroup, shouldSelectMethod, uiValue]);
  const groupsToRender = shouldSelectMethod && effectiveSelectedGroup ? [effectiveSelectedGroup] : undefined;
  const getMethodLabel = (groupId: 'code' | 'totp') => {
    return tOry(`mfa.${groupId}`);
  };
  const selectedMethodLabel = effectiveSelectedGroup ? getMethodLabel(effectiveSelectedGroup) : null;

  // flowが初期化されるまで
  if (!flow || isInitializing) {
    return <div className="flex justify-center py-3">
        <Loader variant="current" size="size-8" />
      </div>;
  }
  return <div className="space-y-4">
      {shouldSelectMethod ? <Select value={effectiveSelectedGroup ?? undefined} onValueChange={value => setSelectedGroup(value as 'code' | 'totp')} disabled={isPending}>
          <SelectTrigger className="w-full">
            {selectedMethodLabel}
          </SelectTrigger>
          <SelectContent>
            {mfaMethods.map(groupId => <SelectItem key={groupId} value={groupId}>
                {getMethodLabel(groupId)}
              </SelectItem>)}
          </SelectContent>
        </Select> : null}

      <OryForm<UpdateLoginFlowBody> ui={flow.ui} disabled={isPending} onSubmit={onSubmit} groups={groupsToRender} />
    </div>;
}